<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="innerVisible"
    width="1160px"
    custom-class="ding-sync-center-dialog"
    append-to-body
    :close-on-click-modal="false"
    @open="handleOpen"
    @closed="handleClosed"
  >
    <div class="sync-center-dialog">
      <div class="sync-center-hero">
        <div>
          <strong>任务进度与结果</strong>
          <p>
            当前学校：{{ displaySchoolName }}
            · 跟随顶部学校切换，仅查看本校同步任务。
          </p>
        </div>
        <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="query">刷新</el-button>
      </div>

      <div class="sync-center-filters">
        <el-select v-model="filters.bigType" clearable placeholder="通讯录类型" class="filter-sm">
          <el-option label="内部通讯录" :value="0" />
          <el-option label="家校通讯录" :value="1" />
        </el-select>
        <el-select v-model="filters.resourceType" clearable placeholder="同步资源" class="filter-sm">
          <el-option label="组织" value="Department" />
          <el-option label="学生" value="Student" />
          <el-option label="教师" value="Teacher" />
          <el-option label="全部" value="All" />
        </el-select>
        <el-select v-model="filters.status" clearable placeholder="任务状态" class="filter-sm">
          <el-option label="待执行" :value="0" />
          <el-option label="执行中" :value="1" />
          <el-option label="成功" :value="2" />
          <el-option label="部分失败" :value="3" />
          <el-option label="失败" :value="4" />
        </el-select>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
        <el-button
          type="danger"
          plain
          icon="el-icon-delete"
          :disabled="!selectedTaskRows.length"
          :loading="batchDeletingTasks"
          @click="batchDeleteTasks"
        >批量删除</el-button>
      </div>

      <div class="sync-center-table-wrap">
        <el-table
          ref="taskTable"
          v-loading="loading"
          :data="rows"
          stripe
          max-height="380"
          empty-text="暂无同步任务"
          class="sync-center-table"
          :row-class-name="taskRowClassName"
          @selection-change="onTaskSelectionChange"
          @row-click="handleTaskRowClick"
        >
          <el-table-column type="selection" width="42" :selectable="canDeleteTask" />
          <el-table-column label="同步范围" min-width="200" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ taskScopeText(row) }}</template>
          </el-table-column>
          <el-table-column label="通讯录" width="88">
            <template slot-scope="{ row }">
              {{ Number(valueOf(row, 'BigType')) === 0 ? '内部' : '家校' }}
            </template>
          </el-table-column>
          <el-table-column label="资源" width="80">
            <template slot-scope="{ row }">{{ resourceText(valueOf(row, 'ResourceType')) }}</template>
          </el-table-column>
          <el-table-column label="进度" min-width="160">
            <template slot-scope="{ row }">
              <el-progress :percentage="progressOf(row)" :status="progressStatus(row)" :stroke-width="10" />
            </template>
          </el-table-column>
          <el-table-column label="成功/失败/跳过" width="130" align="center">
            <template slot-scope="{ row }">
              <span class="success-count">{{ numberOf(row, 'SuccessCount') }}</span>
              /
              <span class="failed-count">{{ numberOf(row, 'FailCount') }}</span>
              /
              <span class="skip-count">{{ numberOf(row, 'SkipCount') }}</span>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="96">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusTag(valueOf(row, 'Status'))">
                {{ statusText(valueOf(row, 'Status')) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="CreateTime" label="提交时间" width="180" />
          <el-table-column label="操作" width="200" fixed="right" align="center">
            <template slot-scope="{ row }">
              <div class="table-op-pair">
                <el-button type="text" @click.stop="openDetail(row)">明细</el-button>
                <el-button
                  v-if="canRetry(row)"
                  type="text"
                  class="retry-action"
                  :loading="retryingId === taskId(row)"
                  @click.stop="retryTask(row)"
                >重试</el-button>
                <el-button
                  v-if="canDeleteTask(row)"
                  type="text"
                  class="delete-action"
                  :loading="taskDeletingId === taskId(row)"
                  @click.stop="deleteTask(row)"
                >删除</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="sync-center-pagination">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :current-page="page"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50]"
          :total="total"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </div>

    <el-dialog
      title="同步任务明细"
      :visible.sync="detailVisible"
      width="900px"
      append-to-body
      custom-class="ding-sync-detail-dialog"
      :close-on-click-modal="false"
    >
      <div class="sync-detail-dialog">
        <div v-if="currentTask" class="task-summary">
          <span><em>资源</em>{{ resourceText(currentTask.ResourceType) }}</span>
          <span><em>状态</em>{{ statusText(currentTask.Status) }}</span>
          <span><em>范围</em>{{ taskScopeText(currentTask) }}</span>
          <span><em>开始时间</em>{{ currentTask.StartTime || '-' }}</span>
          <span><em>完成时间</em>{{ currentTask.FinishTime || '-' }}</span>
        </div>
        <div class="detail-filter">
          <el-radio-group v-model="detailResultFilter" size="small" @change="handleDetailFilterChange">
            <el-radio-button :label="0">全部</el-radio-button>
            <el-radio-button :label="1">成功</el-radio-button>
            <el-radio-button :label="2">失败</el-radio-button>
          </el-radio-group>
          <div class="detail-count-filter" title="留空不过滤；填 0 即只看数量大于 0 的明细">
            <span>数量 &gt;</span>
            <el-input-number
              v-model="detailMinItemCount"
              size="small"
              :min="0"
              :max="999999"
              :precision="0"
              :controls="false"
              placeholder="N"
              @change="handleDetailFilterChange"
              @keyup.enter.native="handleDetailFilterChange"
            />
          </div>
          <el-button
            type="danger"
            plain
            size="small"
            icon="el-icon-delete"
            :disabled="!selectedDetailRows.length || !canDeleteCurrentTask"
            :loading="batchDeletingDetails"
            @click="batchDeleteDetails"
          >批量删除</el-button>
        </div>
        <div class="sync-detail-table-wrap">
          <el-table
            ref="detailTable"
            v-loading="detailLoading"
            :data="detailRows"
            stripe
            height="360"
            empty-text="暂无明细"
            class="sync-detail-table"
            :row-class-name="detailRowClassName"
            @selection-change="onDetailSelectionChange"
            @row-click="handleDetailRowClick"
          >
            <el-table-column type="selection" width="42" :selectable="() => canDeleteCurrentTask" />
            <el-table-column label="资源名称" min-width="140" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ resourceText(valueOf(row, 'ResourceName')) }}</template>
            </el-table-column>
            <el-table-column label="资源标识" min-width="110" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ resourceKeyText(row) }}</template>
            </el-table-column>
            <el-table-column label="结果" width="80" align="center">
              <template slot-scope="{ row }">
                <el-tag size="mini" :type="valueOf(row, 'IsSuccess') ? 'success' : 'danger'">
                  {{ valueOf(row, 'IsSuccess') ? '成功' : '失败' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="90" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ operationText(valueOf(row, 'Operation')) }}</template>
            </el-table-column>
            <el-table-column prop="ItemCount" label="数量" width="70" align="center" />
            <el-table-column prop="ResultMessage" label="处理信息" min-width="180" show-overflow-tooltip />
            <el-table-column prop="CreateTime" label="执行时间" width="156" />
            <el-table-column label="操作" width="90" fixed="right">
              <template slot-scope="{ row }">
                <el-button
                  v-if="canDeleteCurrentTask"
                  type="text"
                  class="delete-action"
                  :loading="detailDeletingId === detailId(row)"
                  @click.stop="deleteDetail(row)"
                >删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="sync-center-pagination">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next"
            :current-page="detailPage"
            :page-size="detailPageSize"
            :page-sizes="[10, 20, 50]"
            :total="detailTotal"
            @current-change="handleDetailPageChange"
            @size-change="handleDetailSizeChange"
          />
        </div>
      </div>
    </el-dialog>
  </el-dialog>
</template>

<script>
import {
  getDingTalkSyncTaskPage,
  getDingTalkSyncTask,
  getDingTalkSyncTaskDetailPage,
  retryDingTalkSyncTask,
  deleteDingTalkSyncTask,
  batchDeleteDingTalkSyncTasks,
  deleteDingTalkSyncTaskDetail,
  batchDeleteDingTalkSyncTaskDetails,
  getTeachingDepartmentTree,
  getInternalDepartmentTree,
  getDepartmentNamesMap,
} from '../api/modules/department'
import {
  flattenDepartmentTree,
  formatTaskScopeText,
  collectMissingScopeDepartmentIds,
} from '../utils/dingTalkSyncScope'

export default {
  name: 'DingTalkSyncTaskCenterDialog',
  props: {
    visible: { type: Boolean, default: false },
    /** 当前学校部门 ID；优先与顶部 activeSchoolId 对齐 */
    schoolDepartmentId: { type: [Number, String], default: '' },
    /** 打开时默认筛选的通讯录类型：0 内部 / 1 家校 */
    bigType: { type: [Number, String], default: '' },
    /** 当前学校名称（展示用） */
    schoolName: { type: String, default: '' },
    title: { type: String, default: '钉钉同步中心' },
  },
  data() {
    return {
      loading: false,
      rows: [],
      page: 1,
      pageSize: 10,
      total: 0,
      filters: this.createEmptyFilters(),
      detailVisible: false,
      detailLoading: false,
      detailRows: [],
      detailPage: 1,
      detailPageSize: 10,
      detailTotal: 0,
      currentTaskId: '',
      currentTask: null,
      detailResultFilter: 0,
      detailMinItemCount: null,
      retryingId: '',
      selectedTaskRows: [],
      selectedDetailRows: [],
      taskDeletingId: '',
      detailDeletingId: '',
      batchDeletingTasks: false,
      batchDeletingDetails: false,
      pollTimer: null,
      scopeDeptFlatByBigType: { 0: [], 1: [] },
      scopeDeptNameById: {},
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      },
    },
    dialogTitle() {
      return this.title || '钉钉同步中心'
    },
    /** 统一取顶部固定学校，与业务页范围一致。最后更新：2026-08-30 */
    resolvedSchoolId() {
      const fromStore = this.$store.state.activeSchoolId
      const fromProp = this.schoolDepartmentId
      const schoolId = fromStore || fromProp
      if (schoolId === null || schoolId === undefined || schoolId === '' || Number(schoolId) <= 0) return ''
      return String(schoolId)
    },
    displaySchoolName() {
      return this.schoolName
        || this.$store.state.activeSchoolName
        || (this.resolvedSchoolId ? `学校 ${this.resolvedSchoolId}` : '未选择学校')
    },
    /** 当前明细所属任务是否允许删除明细 */
    canDeleteCurrentTask() {
      return this.currentTask && this.canDeleteTask(this.currentTask)
    },
  },
  watch: {
    '$store.state.activeSchoolId'(newId, oldId) {
      if (!this.visible) return
      if (String(newId || '') === String(oldId || '')) return
      this.page = 1
      this.query()
    },
  },
  beforeDestroy() {
    this.clearPoll()
  },
  methods: {
    createEmptyFilters() {
      return {
        channel: 'DingDing',
        bigType: '',
        resourceType: '',
        status: '',
      }
    },
    /** 同时兼容后端大小写字段 */
    valueOf(row, key) {
      if (!row) return ''
      const lowerKey = key.charAt(0).toLowerCase() + key.slice(1)
      return row[key] !== undefined ? row[key] : row[lowerKey]
    },
    numberOf(row, key) {
      return Number(this.valueOf(row, key) || 0)
    },
    taskId(row) {
      return this.valueOf(row, 'Id') || this.valueOf(row, 'TaskId')
    },
    detailId(row) {
      return this.valueOf(row, 'Id') || this.valueOf(row, 'DetailId')
    },
    canDeleteTask(row) {
      return [0, 1].indexOf(Number(this.valueOf(row, 'Status'))) === -1
    },
    onTaskSelectionChange(rows) {
      this.selectedTaskRows = rows || []
    },
    /** 点击行切换任务勾选（待执行/执行中不可选）。最后更新：2026-08-30 */
    handleTaskRowClick(row, column, event) {
      if (!row || !this.canDeleteTask(row)) return
      if (event && (event.target.closest('.el-button') || event.target.closest('.el-checkbox'))) return
      const table = this.$refs.taskTable
      if (!table) return
      table.toggleRowSelection(row)
    },
    taskRowClassName({ row }) {
      return this.canDeleteTask(row) ? 'is-row-selectable' : 'is-row-disabled'
    },
    onDetailSelectionChange(rows) {
      this.selectedDetailRows = rows || []
    },
    /** 点击行切换明细勾选。最后更新：2026-08-30 */
    handleDetailRowClick(row, column, event) {
      if (!row || !this.canDeleteCurrentTask) return
      if (event && (event.target.closest('.el-button') || event.target.closest('.el-checkbox'))) return
      const table = this.$refs.detailTable
      if (!table) return
      table.toggleRowSelection(row)
    },
    detailRowClassName() {
      return this.canDeleteCurrentTask ? 'is-row-selectable' : 'is-row-disabled'
    },
    applyDefaultFilters() {
      this.filters = this.createEmptyFilters()
      if (this.bigType === 0 || this.bigType === 1 || this.bigType === '0' || this.bigType === '1') {
        this.filters.bigType = Number(this.bigType)
      }
    },
    async handleOpen() {
      this.page = 1
      this.applyDefaultFilters()
      await Promise.all([this.loadScopeDepartmentIndex(0), this.loadScopeDepartmentIndex(1)])
      await this.query()
      this.startPoll()
    },
    /** 加载组织树索引，供同步范围折叠展示。最后更新：2026-08-30 */
    async loadScopeDepartmentIndex(bigType) {
      const schoolId = Number(this.resolvedSchoolId)
      if (!schoolId) {
        this.$set(this.scopeDeptFlatByBigType, bigType, [])
        return
      }
      try {
        const loader = bigType === 0 ? getInternalDepartmentTree : getTeachingDepartmentTree
        const res = await loader({
          pid: 0,
          scopeDepartmentId: schoolId,
        })
        const tree = res && res.data && res.data.response
        this.$set(this.scopeDeptFlatByBigType, bigType, flattenDepartmentTree(tree))
      } catch (e) {
        this.$set(this.scopeDeptFlatByBigType, bigType, [])
      }
    },
    /** 任务同步范围展示（父节点折叠）。最后更新：2026-08-30 */
    taskScopeText(row) {
      return formatTaskScopeText(row, this.scopeDeptFlatByBigType, this.scopeDeptNameById)
    },
    /** 补全组织树未命中的部门名称。最后更新：2026-08-30 */
    async resolveTaskScopeNames(rows) {
      const missingIds = collectMissingScopeDepartmentIds(
        rows,
        this.scopeDeptFlatByBigType,
        this.scopeDeptNameById
      )
      if (!missingIds.length) return
      const nameMap = await getDepartmentNamesMap(missingIds)
      this.scopeDeptNameById = Object.assign({}, this.scopeDeptNameById, nameMap)
    },
    handleClosed() {
      this.clearPoll()
      this.detailVisible = false
      this.selectedTaskRows = []
      this.selectedDetailRows = []
    },
    startPoll() {
      this.clearPoll()
      this.pollTimer = setInterval(() => {
        if (!this.visible || this.detailVisible) return
        const hasRunning = (this.rows || []).some(row => [0, 1].indexOf(Number(this.valueOf(row, 'Status'))) !== -1)
        if (hasRunning) this.query({ silent: true })
      }, 5000)
    },
    clearPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async query(options = {}) {
      if (!options.silent) this.loading = true
      try {
        const schoolId = this.resolvedSchoolId
        if (!schoolId) {
          this.rows = []
          this.total = 0
          if (!options.silent) this.$message.warning('请先在顶部选择学校')
          return
        }
        const query = {
          PageIndex: this.page,
          PageSize: this.pageSize,
          SchoolDepartmentId: Number(schoolId),
          Channel: this.filters.channel || null,
          BigType: this.filters.bigType === '' ? null : this.filters.bigType,
          ResourceType: this.filters.resourceType || null,
          Status: this.filters.status === '' ? null : this.filters.status,
        }
        const res = await getDingTalkSyncTaskPage(query)
        const response = (res.data && res.data.response) || {}
        this.rows = response.data || response.Data || []
        this.total = Number(response.dataCount || response.DataCount || 0)
        await this.resolveTaskScopeNames(this.rows)
      } finally {
        if (!options.silent) this.loading = false
        this.$nextTick(this.layoutTaskTable)
      }
    },
    /** 固定列与主表行高对齐，避免末行错位与滚动卡顿。最后更新：2026-08-30 */
    layoutTaskTable() {
      const table = this.$refs.taskTable
      if (table && typeof table.doLayout === 'function') table.doLayout()
    },
    layoutDetailTable() {
      const table = this.$refs.detailTable
      if (table && typeof table.doLayout === 'function') table.doLayout()
    },
    handleSearch() {
      this.page = 1
      this.query()
    },
    resetFilters() {
      this.applyDefaultFilters()
      this.handleSearch()
    },
    handlePageChange(page) {
      this.page = page
      this.query()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.page = 1
      this.query()
    },
    progressOf(row) {
      const rawProgress = this.valueOf(row, 'Progress')
      const explicit = Number(rawProgress)
      if (rawProgress !== '' && rawProgress !== null && Number.isFinite(explicit)) {
        return Math.max(0, Math.min(100, explicit))
      }
      const total = this.numberOf(row, 'TotalCount')
      const done = this.numberOf(row, 'SuccessCount') + this.numberOf(row, 'FailCount') + this.numberOf(row, 'SkipCount')
      return total > 0 ? Math.min(100, Math.round((done * 100) / total)) : 0
    },
    progressStatus(row) {
      const status = Number(this.valueOf(row, 'Status'))
      if (status === 4) return 'exception'
      if (status === 2) return 'success'
      return undefined
    },
    resourceText(value) {
      return ({ department: '组织', student: '学生', teacher: '教师', all: '全部' })[String(value).toLowerCase()] || value || '-'
    },
    /** 明细操作中文。最后更新：2026-08-30 */
    operationText(value) {
      return ({
        sync: '同步',
        skipped: '跳过',
        initialize: '初始化',
        retry: '重试',
      })[String(value || '').toLowerCase()] || value || '-'
    },
    /** 资源标识：枚举转中文，其它（如钉钉部门 ID）原样展示 */
    resourceKeyText(row) {
      const key = this.valueOf(row, 'ResourceKey')
      const mapped = this.resourceText(key)
      return mapped === key ? (key || '-') : mapped
    },
    statusText(value) {
      return ({ 0: '待执行', 1: '执行中', 2: '成功', 3: '部分失败', 4: '失败' })[Number(value)] || '-'
    },
    statusTag(value) {
      return ({ 0: 'info', 1: 'warning', 2: 'success', 3: 'warning', 4: 'danger' })[Number(value)] || 'info'
    },
    canRetry(row) {
      return [3, 4].indexOf(Number(this.valueOf(row, 'Status'))) !== -1
    },
    async openDetail(row) {
      this.currentTaskId = this.taskId(row)
      this.detailPage = 1
      this.detailResultFilter = 0
      this.detailMinItemCount = null
      this.selectedDetailRows = []
      this.detailVisible = true
      const taskRes = await getDingTalkSyncTask(this.currentTaskId)
      this.currentTask = taskRes.data && taskRes.data.response
      await this.loadDetail()
    },
    async loadDetail() {
      this.detailLoading = true
      try {
        const res = await getDingTalkSyncTaskDetailPage({
          TaskId: this.currentTaskId,
          ResultFilter: this.detailResultFilter,
          MinItemCount: this.detailMinItemCount === null || this.detailMinItemCount === ''
            ? null
            : Number(this.detailMinItemCount),
          PageIndex: this.detailPage,
          PageSize: this.detailPageSize,
        })
        const response = (res.data && res.data.response) || {}
        this.detailRows = response.data || response.Data || []
        this.detailTotal = Number(response.dataCount || response.DataCount || 0)
      } finally {
        this.detailLoading = false
        this.$nextTick(this.layoutDetailTable)
      }
    },
    handleDetailPageChange(page) {
      this.detailPage = page
      this.loadDetail()
    },
    handleDetailSizeChange(size) {
      this.detailPageSize = size
      this.detailPage = 1
      this.loadDetail()
    },
    handleDetailFilterChange() {
      this.detailPage = 1
      this.loadDetail()
    },
    async retryTask(row) {
      const taskId = this.taskId(row)
      await this.$confirm('仅重试该任务中的失败项，确认继续吗？', '失败重试', { type: 'warning' })
      this.retryingId = taskId
      try {
        const res = await retryDingTalkSyncTask({ TaskId: taskId })
        if (res.data && res.data.success) {
          this.$message.success(res.data.msg || '重试任务已提交')
          this.query()
        } else {
          this.$message.error((res.data && res.data.msg) || '重试失败')
        }
      } finally {
        this.retryingId = ''
      }
    },
    async deleteTask(row) {
      if (!this.canDeleteTask(row)) {
        this.$message.warning('待执行或执行中的任务不可删除')
        return
      }
      const id = this.taskId(row)
      await this.$confirm(`确认删除任务 ${id} 及其全部明细吗？`, '删除任务', { type: 'warning' })
      this.taskDeletingId = id
      try {
        const res = await deleteDingTalkSyncTask({ TaskId: id })
        if (res.data && res.data.success) {
          this.$message.success(res.data.msg || '删除成功')
          if (String(this.currentTaskId) === String(id)) {
            this.detailVisible = false
            this.currentTask = null
            this.currentTaskId = ''
          }
          this.query()
        } else {
          this.$message.error((res.data && res.data.msg) || '删除失败')
        }
      } finally {
        this.taskDeletingId = ''
      }
    },
    async batchDeleteTasks() {
      if (!this.selectedTaskRows.length) return
      const ids = this.selectedTaskRows.map((row) => this.taskId(row)).filter(Boolean)
      if (!ids.length) return
      await this.$confirm(`确认删除选中的 ${ids.length} 条任务及其全部明细吗？`, '批量删除', { type: 'warning' })
      this.batchDeletingTasks = true
      try {
        const res = await batchDeleteDingTalkSyncTasks({ TaskIds: ids })
        if (res.data && res.data.success) {
          this.$message.success(res.data.msg || '删除成功')
          if (ids.some((id) => String(id) === String(this.currentTaskId))) {
            this.detailVisible = false
            this.currentTask = null
            this.currentTaskId = ''
          }
          this.selectedTaskRows = []
          this.query()
        } else {
          this.$message.error((res.data && res.data.msg) || '删除失败')
        }
      } finally {
        this.batchDeletingTasks = false
      }
    },
    async deleteDetail(row) {
      if (!this.canDeleteCurrentTask) {
        this.$message.warning('任务执行中，暂不可删除明细')
        return
      }
      const id = this.detailId(row)
      await this.$confirm('确认删除该条明细吗？', '删除明细', { type: 'warning' })
      this.detailDeletingId = id
      try {
        const res = await deleteDingTalkSyncTaskDetail({ DetailId: id })
        if (res.data && res.data.success) {
          this.$message.success(res.data.msg || '删除成功')
          const taskRes = await getDingTalkSyncTask(this.currentTaskId)
          this.currentTask = taskRes.data && taskRes.data.response
          await this.loadDetail()
          this.query({ silent: true })
        } else {
          this.$message.error((res.data && res.data.msg) || '删除失败')
        }
      } finally {
        this.detailDeletingId = ''
      }
    },
    async batchDeleteDetails() {
      if (!this.selectedDetailRows.length || !this.canDeleteCurrentTask) return
      const ids = this.selectedDetailRows.map((row) => this.detailId(row)).filter(Boolean)
      if (!ids.length) return
      await this.$confirm(`确认删除选中的 ${ids.length} 条明细吗？`, '批量删除', { type: 'warning' })
      this.batchDeletingDetails = true
      try {
        const res = await batchDeleteDingTalkSyncTaskDetails({
          TaskId: this.currentTaskId,
          DetailIds: ids,
        })
        if (res.data && res.data.success) {
          this.$message.success(res.data.msg || '删除成功')
          const taskRes = await getDingTalkSyncTask(this.currentTaskId)
          this.currentTask = taskRes.data && taskRes.data.response
          this.selectedDetailRows = []
          await this.loadDetail()
          this.query({ silent: true })
        } else {
          this.$message.error((res.data && res.data.msg) || '删除失败')
        }
      } finally {
        this.batchDeletingDetails = false
      }
    },
  },
}
</script>

<style scoped>
.sync-center-dialog {
  min-height: 280px;
}

.sync-center-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid #dce8f8;
  border-radius: 10px;
  background: #f5f9ff;
}

.sync-center-hero strong {
  display: block;
  color: #1e293b;
  font-size: 14px;
}

.sync-center-hero p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.sync-center-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.filter-sm {
  width: 128px;
}

.sync-center-table-wrap {
  overflow: hidden;
  border: 1px solid #e8edf3;
  border-radius: 10px;
}

.sync-center-table {
  width: 100%;
}

.sync-center-table >>> .el-table__body tr.is-row-selectable {
  cursor: pointer;
}

.sync-center-table >>> .el-table__body tr.is-row-disabled {
  cursor: not-allowed;
}

.sync-center-table >>> .el-table__body tr.is-row-disabled:hover > td {
  background: inherit !important;
}

.sync-center-pagination {
  margin-top: 16px;
  padding-top: 4px;
  text-align: right;
}

.success-count {
  color: #67c23a;
  font-weight: 600;
}

.failed-count,
.retry-action {
  color: #f56c6c;
  font-weight: 600;
}

.delete-action,
.delete-action:focus,
.delete-action:hover {
  color: #f56c6c !important;
}

.skip-count {
  color: #909399;
}

.sync-detail-dialog {
  min-height: 200px;
}

.task-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin-bottom: 14px;
  border: 1px solid #e8edf3;
  border-radius: 10px;
  overflow: hidden;
  background: #fbfcfe;
}

.task-summary span {
  padding: 10px 12px;
  border-right: 1px solid #e8edf3;
  border-bottom: 1px solid #e8edf3;
  color: #334155;
  font-size: 13px;
}

.task-summary em {
  margin-right: 8px;
  color: #94a3b8;
  font-style: normal;
}

.detail-filter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.detail-count-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #606266;
  font-size: 13px;
}

.detail-count-filter .el-input-number {
  width: 96px;
}

.sync-detail-table-wrap {
  overflow: hidden;
  border: 1px solid #e8edf3;
  border-radius: 10px;
}

.sync-detail-table {
  width: 100%;
}
</style>

<style>
.ding-sync-center-dialog {
  max-width: calc(100vw - 32px);
  border-radius: 12px;
  overflow: hidden;
}

.ding-sync-center-dialog .el-dialog__header {
  padding: 16px 20px 12px;
  border-bottom: 1px solid #e8edf3;
  background: #fbfcfe;
}

.ding-sync-center-dialog .el-dialog__title {
  color: #1e293b;
  font-size: 16px;
  font-weight: 600;
}

.ding-sync-center-dialog .el-dialog__body {
  padding: 14px 18px 16px;
}

.ding-sync-center-dialog .el-dialog__headerbtn {
  top: 16px;
  right: 18px;
}

.ding-sync-detail-dialog {
  max-width: calc(100vw - 32px);
  border-radius: 12px;
  overflow: hidden;
}

.ding-sync-detail-dialog .el-dialog__header {
  padding: 16px 20px 12px;
  border-bottom: 1px solid #e8edf3;
  background: #fbfcfe;
}

.ding-sync-detail-dialog .el-dialog__title {
  color: #1e293b;
  font-size: 16px;
  font-weight: 600;
}

.el-dialog.ding-sync-detail-dialog .el-dialog__body {
  padding: 14px 18px 16px;
  /* 明细表固定 height 滚动，body 不再叠一层滚动条。最后修改：2026-09-14 */
  max-height: none;
  overflow: hidden !important;
}

.el-dialog.ding-sync-detail-dialog .el-dialog__headerbtn {
  top: 16px;
  right: 18px;
}

/* 右侧固定列：跟主表同步滚，自身不显示纵/横滚动条，避免双条。最后修改：2026-09-14 */
.el-dialog.ding-sync-detail-dialog .el-table__fixed-body-wrapper,
.el-dialog.ding-sync-detail-dialog .el-table__fixed-right .el-table__fixed-body-wrapper {
  overflow: hidden !important;
}

.el-dialog.ding-sync-detail-dialog .sync-detail-table-wrap {
  overflow: hidden;
}
</style>
